'use client'

import React, { useState, useCallback, useEffect, createContext, useContext } from 'react'
import { CallBackProps, STATUS, Step } from 'react-joyride-react-19'
import Joyride from 'react-joyride-react-19'
import { useAuth } from '@/contexts/AuthContext'
import { ALL_TOURS, TourDefinition, TourUtils } from '@/config/tours'
import { joyrideStyles, defaultJoyrideConfig } from '@/config/joyride-theme'

// Hook para detectar tema escuro
function useTheme() {
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    // Verificar tema inicial
    const checkTheme = () => {
      const isDarkMode = document.documentElement.classList.contains('dark')
      setIsDark(isDarkMode)
    }

    // Verificar inicialmente
    checkTheme()

    // Observer para mudanças no tema
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          checkTheme()
        }
      })
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })

    return () => observer.disconnect()
  }, [])

  return { isDark }
}

// Estilos dinâmicos baseados no tema
function getDynamicJoyrideStyles(isDark: boolean) {
  return {
    ...joyrideStyles,
    options: {
      ...joyrideStyles.options,
      primaryColor: '#A9744F',
      backgroundColor: isDark ? '#1f2937' : '#ffffff',
      textColor: isDark ? '#f9fafb' : '#1f2937',
      overlayColor: 'rgba(0, 0, 0, 0)', // Overlay com opacidade ZERO
      arrowColor: isDark ? '#1f2937' : '#ffffff',
      zIndex: 1400,
    },
    tooltip: {
      ...joyrideStyles.tooltip,
      backgroundColor: isDark ? '#1f2937' : '#ffffff',
      border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
      color: isDark ? '#f9fafb' : '#1f2937',
      boxShadow: isDark 
        ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 8px 16px -4px rgba(0, 0, 0, 0.3)'
        : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 8px 16px -4px rgba(0, 0, 0, 0.1)',
      zIndex: 1500, // Tooltip por cima
    },
    tooltipTitle: {
      ...joyrideStyles.tooltipTitle,
      color: isDark ? '#f9fafb' : '#1f2937',
    },
    tooltipContent: {
      ...joyrideStyles.tooltipContent,
      color: isDark ? '#d1d5db' : '#6b7280',
    },
    tooltipFooter: {
      ...joyrideStyles.tooltipFooter,
      borderTop: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
    },
    buttonBack: {
      ...joyrideStyles.buttonBack,
      backgroundColor: isDark ? '#374151' : '#f9fafb',
      border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`,
      color: isDark ? '#f9fafb' : '#374151',
    },
    buttonSkip: {
      ...joyrideStyles.buttonSkip,
      color: isDark ? '#9ca3af' : '#6b7280',
    },
    buttonClose: {
      ...joyrideStyles.buttonClose,
      color: isDark ? '#9ca3af' : '#6b7280',
    },
    beacon: {
      ...joyrideStyles.beacon,
      border: `3px solid ${isDark ? '#1f2937' : '#ffffff'}`,
      boxShadow: isDark
        ? '0 0 0 6px rgba(59, 130, 246, 0.3), 0 4px 12px rgba(59, 130, 246, 0.6)'
        : '0 0 0 6px rgba(59, 130, 246, 0.2), 0 4px 12px rgba(59, 130, 246, 0.4)',
      zIndex: 1300, // Beacon visível
    },
    beaconOuter: {
      ...joyrideStyles.beaconOuter,
      border: `2px solid ${isDark ? '#1f2937' : '#ffffff'}`,
    },
    overlay: {
      ...joyrideStyles.overlay,
      backgroundColor: 'rgba(0, 0, 0, 0)', // Opacidade ZERO
      zIndex: 1400,
    },
    overlayLegacy: {
      ...joyrideStyles.overlayLegacy,
      backgroundColor: 'rgba(0, 0, 0, 0)', // Opacidade ZERO
      zIndex: 1400,
    },
    spotlight: {
      ...joyrideStyles.spotlight,
      border: isDark ? '4px solid #CBBCA9' : '4px solid #A9744F', // Borda super grossa
      boxShadow: isDark 
        ? '0 0 0 6px rgba(96, 165, 250, 0.5), 0 0 0 10px rgba(96, 165, 250, 0.3), 0 0 30px rgba(96, 165, 250, 0.4)'
        : '0 0 0 6px rgba(59, 130, 246, 0.4), 0 0 0 10px rgba(59, 130, 246, 0.2), 0 0 30px rgba(59, 130, 246, 0.3)',
    },
  }
}

interface TourState {
  isRunning: boolean
  currentTourId: string | null
  steps: Step[]
}

interface TourContextType {
  isRunning: boolean
  currentTourId: string | null
  steps: Step[]
  startTour: (tourId: string, force?: boolean) => boolean
  stopTour: () => void
  autoStartTour: (tourId: string, conditions?: { hasData?: boolean; waitTime?: number }) => (() => void) | undefined
  handleJoyrideCallback: (data: CallBackProps) => void
  hasViewedTour: (tourId: string) => boolean
  markTourAsViewed: (tourId: string) => Promise<void>
  resetTour: (tourId: string) => Promise<void>
  resetAllTours: () => Promise<void>
  getTourInfo: (tourId: string) => any
  getAllToursStatus: () => any[]
  // Para compatibilidade
  runTour: boolean
  tourConfig: { showTour: boolean; steps: Step[] }
}

// Context
const TourContext = createContext<TourContextType | undefined>(undefined)

/**
 * 🎯 TOUR PROVIDER COMPONENT
 * 
 * Provider que gerencia todos os tours do sistema e renderiza o Joyride
 */
export function TourProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { profile, updateProfile } = useAuth()
  const { isDark } = useTheme()
  const [tourState, setTourState] = useState<TourState>({
    isRunning: false,
    currentTourId: null,
    steps: []
  })

  // Estilos dinâmicos baseados no tema
  const dynamicStyles = getDynamicJoyrideStyles(isDark)

  // Verificar se usuário já viu um tour específico
  const hasViewedTour = useCallback((tourId: string): boolean => {
    return profile?.viewedTours?.includes(tourId) ?? false
  }, [profile?.viewedTours])

  // Marcar tour como visto
  const markTourAsViewed = useCallback(async (tourId: string) => {
    if (!profile || hasViewedTour(tourId)) return

    try {
      const updatedViewedTours = [...(profile.viewedTours || []), tourId]
      await updateProfile({ viewedTours: updatedViewedTours })
      console.log(`✅ Tour marcado como visto: ${tourId}`)
    } catch (error) {
      console.error('Erro ao marcar tour como visto:', error)
    }
  }, [profile, hasViewedTour, updateProfile])

  // Iniciar tour específico
  const startTour = useCallback((tourId: string, force: boolean = false): boolean => {
    const tour = TourUtils.getTour(tourId)
    
    if (!tour) {
      console.error(`❌ Tour não encontrado: ${tourId}`)
      return false
    }

    if (!force && hasViewedTour(tourId)) {
      console.log(`🔕 Tour já visualizado: ${tourId}`)
      return false
    }

    setTourState({
      isRunning: true,
      currentTourId: tourId,
      steps: tour.steps
    })

    console.log(`🎯 Iniciando tour: ${tour.name} (${tourId})`)
    return true
  }, [hasViewedTour])

  // Parar tour atual
  const stopTour = useCallback(() => {
    setTourState({
      isRunning: false,
      currentTourId: null,
      steps: []
    })
  }, [])

  // Callback do joyride
  const handleJoyrideCallback = useCallback(async (data: CallBackProps) => {
    const { status, action } = data

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      // Marcar tour como visto se foi concluído
      if (tourState.currentTourId && status === STATUS.FINISHED) {
        await markTourAsViewed(tourState.currentTourId)
      }

      // Limpar estado
      stopTour()
    }
  }, [tourState.currentTourId, markTourAsViewed, stopTour])

  // Auto-iniciar tour baseado em condições
  const autoStartTour = useCallback((
    tourId: string, 
    conditions?: { hasData?: boolean; waitTime?: number }
  ) => {
    const tour = TourUtils.getTour(tourId)
    
    if (!tour || !tour.autoStart || hasViewedTour(tourId)) {
      return
    }

    // Verificar condições específicas
    if (conditions?.hasData === false && tour.conditions?.requiresData) {
      return // Tour requer dados mas não há dados
    }

    const waitTime = conditions?.waitTime ?? tour.conditions?.minWaitTime ?? 1000

    const timer = setTimeout(() => {
      startTour(tourId)
    }, waitTime)

    return () => clearTimeout(timer)
  }, [hasViewedTour, startTour])

  // Resetar tour específico (útil para debug/testes)
  const resetTour = useCallback(async (tourId: string) => {
    if (!profile) return

    try {
      const updatedViewedTours = (profile.viewedTours || []).filter(id => id !== tourId)
      await updateProfile({ viewedTours: updatedViewedTours })
      console.log(`🔄 Tour resetado: ${tourId}`)
    } catch (error) {
      console.error('Erro ao resetar tour:', error)
    }
  }, [profile, updateProfile])

  // Resetar todos os tours (admin/debug)
  const resetAllTours = useCallback(async () => {
    if (!profile) return

    try {
      await updateProfile({ viewedTours: [] })
      console.log(`🔄 Todos os tours resetados`)
    } catch (error) {
      console.error('Erro ao resetar tours:', error)
    }
  }, [profile, updateProfile])

  // Obter informações sobre tours
  const getTourInfo = useCallback((tourId: string) => {
    const tour = TourUtils.getTour(tourId)
    if (!tour) return null

    return {
      ...tour,
      hasViewed: hasViewedTour(tourId),
      canStart: !hasViewedTour(tourId)
    }
  }, [hasViewedTour])

  // Listar todos os tours com status
  const getAllToursStatus = useCallback(() => {
    return TourUtils.getAllTourIds().map((tourId: string) => {
      const tour = TourUtils.getTour(tourId)!
      return {
        id: tourId,
        name: tour.name,
        description: tour.description,
        hasViewed: hasViewedTour(tourId),
        canStart: !hasViewedTour(tourId),
        autoStart: tour.autoStart
      }
    })
  }, [hasViewedTour])

  // Valor do contexto
  const contextValue: TourContextType = {
    isRunning: tourState.isRunning,
    currentTourId: tourState.currentTourId,
    steps: tourState.steps,
    startTour,
    stopTour,
    autoStartTour,
    handleJoyrideCallback,
    hasViewedTour,
    markTourAsViewed,
    resetTour,
    resetAllTours,
    getTourInfo,
    getAllToursStatus,
    runTour: tourState.isRunning,
    tourConfig: {
      showTour: tourState.isRunning,
      steps: tourState.steps
    }
  }

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      
      {/* Joyride Global */}
      <Joyride
        steps={tourState.steps}
        run={tourState.isRunning}
        callback={handleJoyrideCallback}
        {...defaultJoyrideConfig}
        styles={dynamicStyles}
        floaterProps={{
          disableAnimation: false,
          styles: {
            floater: {
              filter: 'none !important',
              zIndex: 1500
            }
          }
        }}
      />
    </TourContext.Provider>
  )
}

/**
 * 🎯 HOOK PARA USAR TOURS
 * 
 * Hook que usa o contexto dos tours
 */
export function useTour() {
  const context = useContext(TourContext)
  if (!context) {
    throw new Error('useTour deve ser usado dentro de um TourProvider')
  }
  return context
}

/**
 * 🎯 HOOK ESPECÍFICO PARA TOURS DE ANEXOS
 * Mantém compatibilidade e facilita o uso nos componentes de anexos
 */
export function useAttachmentsTours() {
  const tour = useTour()

  // Método específico para tour do ClaimDetails
  const startClaimDetailsTour = useCallback((force: boolean = false) => {
    return tour.startTour('claim-details-attachments', force)
  }, [tour])

  // Método específico para tour da lixeira
  const startTrashPageTour = useCallback((force: boolean = false) => {
    return tour.startTour('trash-page-tabs', force)
  }, [tour])

  // Auto-start para ClaimDetails - sempre mostra o tour
  const autoStartClaimDetailsTour = useCallback((hasAttachments: boolean) => {
    return tour.autoStartTour('claim-details-attachments', { 
      hasData: true, // Sempre true - tour aparece independente de anexos
      waitTime: 1500 
    })
  }, [tour])

  // Auto-start para trash page
  const autoStartTrashPageTour = useCallback(() => {
    return tour.autoStartTour('trash-page-tabs', { 
      hasData: true,
      waitTime: 1000 
    })
  }, [tour])

  // Método específico para tour de prestadores
  const startPrestadoresTour = useCallback((force: boolean = false) => {
    return tour.startTour('prestadores-manager', force)
  }, [tour])

  // Auto-start para prestadores
  const autoStartPrestadoresTour = useCallback(() => {
    return tour.autoStartTour('prestadores-manager', { 
      hasData: true,
      waitTime: 1200 
    })
  }, [tour])

  // Método específico para tour do mapa de prestadores
  const startMapTour = useCallback((force: boolean = false) => {
    return tour.startTour('map-prestadores', force)
  }, [tour])

  // Auto-start para mapa
  const autoStartMapTour = useCallback(() => {
    return tour.autoStartTour('map-prestadores', { 
      hasData: true,
      waitTime: 1000 
    })
  }, [tour])

  // Método específico para tour de photo captions
  const startPhotoCaptionsTour = useCallback((force: boolean = false) => {
    return tour.startTour('photo-captions', force)
  }, [tour])

  // Auto-start para photo captions
  const autoStartPhotoCaptionsTour = useCallback(() => {
    return tour.autoStartTour('photo-captions', { 
      hasData: true,
      waitTime: 1500 
    })
  }, [tour])

  // Método específico para tour de filtros de claims
  const startClaimsFiltersTour = useCallback((force: boolean = false) => {
    return tour.startTour('claims-filters', force)
  }, [tour])

  // Auto-start para filtros de claims
  const autoStartClaimsFiltersTour = useCallback(() => {
    return tour.autoStartTour('claims-filters', { 
      hasData: true,
      waitTime: 1500 
    })
  }, [tour])

  // Método específico para tour de configuração de colunas
  const startColumnConfiguratorTour = useCallback((force: boolean = false) => {
    return tour.startTour('column-configurator', force)
  }, [tour])

  // Auto-start para configuração de colunas
  const autoStartColumnConfiguratorTour = useCallback(() => {
    return tour.autoStartTour('column-configurator', { 
      hasData: true,
      waitTime: 1000 
    })
  }, [tour])

  // Método específico para tour do botão de distribuição
  const startDistributionButtonTour = useCallback((force: boolean = false) => {
    return tour.startTour('distribution-button', force)
  }, [tour])

  // Auto-start para botão de distribuição
  const autoStartDistributionButtonTour = useCallback(() => {
    return tour.autoStartTour('distribution-button', { 
      hasData: true,
      waitTime: 2000 
    })
  }, [tour])

  // Método específico para tour do menu de tracks
  const startTracksMenuTour = useCallback((force: boolean = false) => {
    return tour.startTour('tracks-menu', force)
  }, [tour])

  // Auto-start para menu de tracks
  const autoStartTracksMenuTour = useCallback(() => {
    return tour.autoStartTour('tracks-menu', { 
      hasData: true,
      waitTime: 1500 
    })
  }, [tour])

  return {
    ...tour,
    startClaimDetailsTour,
    startTrashPageTour,
    autoStartClaimDetailsTour,
    autoStartTrashPageTour,
    startPrestadoresTour,
    autoStartPrestadoresTour,
    startMapTour,
    autoStartMapTour,
    startPhotoCaptionsTour,
    autoStartPhotoCaptionsTour,
    startClaimsFiltersTour,
    autoStartClaimsFiltersTour,
    startColumnConfiguratorTour,
    autoStartColumnConfiguratorTour,
    startDistributionButtonTour,
    autoStartDistributionButtonTour,
    startTracksMenuTour,
    autoStartTracksMenuTour
  }
}
